'use client'

import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type OnChangeFn,
  type PaginationState,
  type RowData,
  type RowModel,
  type SortingState,
  type Updater,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table'
import { ArrowDownNarrowWide, ArrowUpNarrowWide } from 'lucide-react'
import { type ReactNode, useCallback, useMemo } from 'react'

import { cn } from '@/lib/cn'
import type { Sort } from '@/types/sort'
import { DATA_TABLE_ITEMS_PER_PAGE } from '@/utils/constants'
import { getPaginationPages } from '@/utils/getPaginationPages'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../pagination'
import { Skeleton } from '../skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../table/table'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../tooltip'

type DataTableProps<TData extends RowData> = {
  columns: ColumnDef<TData>[]
  data: TData[]
  pageCount: number
  resultsCount: number
  pagination: {
    currentPage: number
    onPageChange: (page: number) => void
  }
  sorting?: {
    currentSorting: Sort<keyof TData> | null
    onSorting: (sort: Sort<keyof TData> | null) => void
  }
  columnVisibility?: VisibilityState
  loading?: boolean
  emptyDataContainer?: ReactNode
}

const DataTableBody = <TData extends RowData>({
  columns,
  loading,
  rowModel,
  emptyDataContainer,
}: Pick<DataTableProps<TData>, 'loading' | 'columns' | 'emptyDataContainer'> & {
  rowModel: RowModel<TData>
}) => {
  if (loading) {
    return (
      <TableRow>
        {Array.from({ length: columns.length }).map((_, index) => (
          <TableCell key={index} className="px-6 py-3">
            <Skeleton className="h-6 w-full" />
          </TableCell>
        ))}
      </TableRow>
    )
  }

  if (!rowModel.rows.length)
    return (
      <TableRow>
        <TableCell
          colSpan={columns.length}
          className={cn(!emptyDataContainer && 'h-24 text-center')}
        >
          {emptyDataContainer ?? (
            <span className="text-lg font-bold text-system-text-secondary">
              Sem dados
            </span>
          )}
        </TableCell>
      </TableRow>
    )

  return rowModel.rows.map((row) => (
    <TableRow
      key={row.id}
      data-state={row.getIsSelected() && 'selected'}
      className="text-sm font-medium text-system-text-primary"
    >
      {row.getVisibleCells().map((cell) => (
        <TableCell key={cell.id} className="px-6 py-3 ">
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </TableCell>
      ))}
    </TableRow>
  ))
}
DataTableBody.displayName = 'TableBody'

const DataTable = <TData extends RowData>({
  columns,
  data,
  pageCount,
  resultsCount,
  pagination,
  sorting,
  loading,
  emptyDataContainer,
  columnVisibility,
}: DataTableProps<TData>) => {
  const { currentSorting, onSorting } = sorting || {}
  const { currentPage, onPageChange } = pagination

  const onSortingChange: OnChangeFn<SortingState> = useCallback(
    (updaterOrValue: Updater<SortingState>) => {
      if (!onSorting) return
      const [sort] =
        typeof updaterOrValue === 'function'
          ? updaterOrValue([
              {
                desc: currentSorting?.direction === 'desc',
                id: String(currentSorting?.field),
              },
            ])
          : updaterOrValue

      if (!sort) {
        onSorting(null)
        return
      }

      onSorting({
        direction: sort.desc ? 'desc' : 'asc',
        field: sort.id as keyof TData,
      })
    },
    [currentSorting?.direction, currentSorting?.field, onSorting],
  )

  const onPaginationChange: OnChangeFn<PaginationState> = useCallback(
    (updaterOrValue: Updater<PaginationState>) => {
      const page =
        typeof updaterOrValue === 'function'
          ? updaterOrValue({
              pageIndex: currentPage - 1,
              pageSize: DATA_TABLE_ITEMS_PER_PAGE,
            }).pageIndex
          : updaterOrValue.pageIndex

      onPageChange(page + 1)
    },
    [currentPage, onPageChange],
  )

  const table = useReactTable({
    data,
    columns,
    pageCount,
    state: {
      sorting: [
        {
          id: String(currentSorting?.field),
          desc: currentSorting?.direction === 'desc',
        },
      ],
      pagination: {
        pageIndex: currentPage - 1,
        pageSize: DATA_TABLE_ITEMS_PER_PAGE,
      },
      columnVisibility,
    },
    enableSorting: !!sorting,
    manualPagination: true,
    manualSorting: true,
    onSortingChange,
    onPaginationChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  const tooltipText = (value: 'asc' | 'desc' | false) => {
    if (value === 'asc') return 'Ordenar de forma de crescente'
    if (value === 'desc') return 'Ordenar de forma decrescente'

    return 'Limpar ordenação'
  }

  const paginationPages = useMemo(
    () => getPaginationPages(currentPage, pageCount),
    [currentPage, pageCount],
  )

  return (
    <div className="flex flex-col justify-between gap-4">
      <Table className="bg-white">
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow
              key={headerGroup.id}
              className="bg-system-background-grey"
            >
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  colSpan={header.colSpan}
                  onClick={header.column.getToggleSortingHandler()}
                  className={cn(
                    'text-system-text-secondary text-sm font-medium px-6 py-3 w-fit',
                    header.column.getCanSort()
                      ? 'cursor-pointer select-none'
                      : '',
                  )}
                >
                  {header.isPlaceholder ? null : header.column.getCanSort() ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <div className="inline-flex items-center gap-2">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                            {{
                              asc: <ArrowDownNarrowWide size={20} />,
                              desc: <ArrowUpNarrowWide size={20} />,
                            }[header.column.getIsSorted() as string] ?? null}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          {tooltipText(header.column.getNextSortingOrder())}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <div className="inline-flex items-center gap-2">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                      {{
                        asc: <ArrowDownNarrowWide size={20} />,
                        desc: <ArrowUpNarrowWide size={20} />,
                      }[header.column.getIsSorted() as string] ?? null}
                    </div>
                  )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          <DataTableBody
            columns={columns}
            rowModel={table.getRowModel()}
            emptyDataContainer={emptyDataContainer}
            loading={loading}
          />
        </TableBody>
      </Table>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 text-sm text-system-text-secondary">
          <span>{resultsCount}</span> <span>resultados</span>
        </div>
        <Pagination>
          <PaginationContent>
            <PaginationItem
              isDisabled={!table.getCanPreviousPage()}
              onClick={table.previousPage}
            >
              <PaginationPrevious />
            </PaginationItem>

            {paginationPages.map((item, i) =>
              item === 'ellipsis' ? (
                <PaginationItem key={`ellipsis-${i}`}>
                  <PaginationEllipsis />
                </PaginationItem>
              ) : (
                <PaginationItem key={item} onClick={() => onPageChange(item)}>
                  <PaginationLink isActive={currentPage === item}>
                    {item}
                  </PaginationLink>
                </PaginationItem>
              ),
            )}

            <PaginationItem
              isDisabled={!table.getCanNextPage()}
              onClick={table.nextPage}
            >
              <PaginationNext />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    </div>
  )
}
DataTable.displayName = 'DataTable'

export { DataTable }
